import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ArrowUpRight, Home, Package, Eye, EyeOff, BookOpen, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import InventoryCard from '@/components/InventoryCard';
import UserHeader from '@/components/UserHeader';
import OrderBook from '@/components/OrderBook';
import PartySummary from '@/components/PartySummary';

// Lazy load heavy modal components
const AddStockModal = lazy(() => import('@/components/AddStockModal'));
const DispatchModal = lazy(() => import('@/components/DispatchModal'));
const ItemDetailModal = lazy(() => import('@/components/ItemDetailModal'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

// Modal loading placeholder
const ModalLoader = () => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 shadow-2xl">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  </div>
);

const SwatchesDepartment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddStock, setShowAddStock] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Track fetch to prevent double calls
  const fetchedRef = useRef(false);
  const lastFetchParamsRef = useRef('');

  // Direct fetch function - no useCallback to avoid dependency issues
  const fetchInventory = async (pageNum = 1, discontinued = false, append = false) => {
    const fetchKey = `${pageNum}-${discontinued}`;
    
    // Prevent duplicate fetches
    if (fetchKey === lastFetchParamsRef.current && !append) {
      return;
    }
    
    const startTime = performance.now();
    console.log(`[Swatch] Fetching inventory page=${pageNum} discontinued=${discontinued}`);
    
    try {
      if (!append) setLoading(true);
      
      const response = await axios.get(
        `${API}/swatch/inventory?include_discontinued=${discontinued}&page=${pageNum}&page_size=${PAGE_SIZE}`
      );
      
      const apiTime = performance.now() - startTime;
      console.log(`[Swatch] API response in ${apiTime.toFixed(0)}ms, items: ${response.data.items.length}`);
      
      const data = response.data;
      lastFetchParamsRef.current = fetchKey;
      
      // Update state in single batch
      if (append) {
        setInventory(prev => [...prev, ...data.items]);
      } else {
        setInventory(data.items);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageNum);
      setLoading(false);
      
      const renderTime = performance.now() - startTime;
      console.log(`[Swatch] Total render time: ${renderTime.toFixed(0)}ms`);
    } catch (error) {
      console.error('[Swatch] Fetch error:', error);
      toast.error('Failed to load inventory');
      setLoading(false);
    }
  };

  // Initial fetch - runs only once
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchInventory(1, showDiscontinued);
    }
  }, []);

  // Handle discontinued toggle
  useEffect(() => {
    if (fetchedRef.current) {
      fetchInventory(1, showDiscontinued);
    }
  }, [showDiscontinued]);

  // Memoized filtered inventory - no heavy operations
  const filteredInventory = useMemo(() => {
    if (!searchQuery.trim()) return inventory;
    const query = searchQuery.toLowerCase();
    return inventory.filter(
      (item) =>
        item.swatch_code.toLowerCase().includes(query) ||
        item.fabric_name.toLowerCase().includes(query)
    );
  }, [searchQuery, inventory]);

  // Simple handlers
  const handleAddStockSuccess = useCallback(() => {
    fetchInventory(1, showDiscontinued);
    setShowAddStock(false);
    toast.success('Stock added successfully');
  }, [showDiscontinued]);

  const handleDispatchSuccess = useCallback(() => {
    fetchInventory(page, showDiscontinued);
    setShowDispatch(false);
    toast.success('Movement recorded successfully');
  }, [page, showDiscontinued]);

  const handleItemClick = useCallback((item) => setSelectedItem(item), []);
  const handleCloseAddStock = useCallback(() => setShowAddStock(false), []);
  const handleCloseDispatch = useCallback(() => setShowDispatch(false), []);
  const handleCloseDetail = useCallback(() => setSelectedItem(null), []);
  
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchInventory(page + 1, showDiscontinued, true);
    }
  }, [hasMore, loading, page, showDiscontinued]);

  const handleRefresh = useCallback(() => {
    fetchInventory(page, showDiscontinued);
  }, [page, showDiscontinued]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="sticky top-0 bg-slate-50 pb-6 pt-4 z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                data-testid="home-button"
                className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
              >
                <Home className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                  Swatches Department
                </h1>
                <p className="text-sm text-slate-500 font-mono">Measured in Pieces</p>
              </div>
            </div>
            <div className="flex gap-3">
              {activeTab === 'inventory' && (
                <>
                  <button
                    onClick={() => setShowAddStock(true)}
                    data-testid="add-stock-button"
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-md shadow-emerald-600/20 transition-colors font-medium"
                  >
                    <Plus className="w-5 h-5" />
                    Add Stock
                  </button>
                  <button
                    onClick={() => setShowDispatch(true)}
                    data-testid="dispatch-button"
                    className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-600/20 transition-colors font-medium"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                    Dispatch
                  </button>
                </>
              )}
              <UserHeader />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6">
            <button
              onClick={() => setActiveTab('inventory')}
              data-testid="tab-inventory"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'inventory'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Package className="w-4 h-4" />
              Inventory
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              data-testid="tab-orders"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'orders'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Order Book
            </button>
            <button
              onClick={() => setActiveTab('party')}
              data-testid="tab-party"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'party'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="w-4 h-4" />
              Party Summary
            </button>
          </div>

          {/* Inventory Tab Search & Filters */}
          {activeTab === 'inventory' && (
            <>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by Swatch Code or Fabric Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full pl-12 h-12 text-base rounded-full border border-slate-200 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setShowDiscontinued(!showDiscontinued)}
                  data-testid="toggle-discontinued"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    showDiscontinued
                      ? 'bg-slate-700 text-white border-slate-700'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {showDiscontinued ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {showDiscontinued ? 'Hiding' : 'Show'} Discontinued Items
                  </span>
                </button>
                
                <span className="text-sm text-slate-500">
                  Showing {filteredInventory.length} of {total} items
                </span>
              </div>
            </>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'inventory' && (
          <>
            {loading && inventory.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading inventory...</p>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-2">No items found</p>
            <p className="text-slate-400 text-sm">Add your first stock item to get started</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {filteredInventory.map((item) => (
                <InventoryCard
                  key={item.id}
                  item={item}
                  type="SWATCH"
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
            
            {hasMore && !searchQuery && (
              <div className="mt-8 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-6 py-3 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Loading...' : `Load More (${inventory.length} of ${total})`}
                </button>
              </div>
            )}
          </>
        )}
          </>
        )}

        {activeTab === 'orders' && (
          <OrderBook type="Swatch" />
        )}

        {activeTab === 'party' && (
          <PartySummary type="Swatch" />
        )}
      </div>

      {showAddStock && (
        <Suspense fallback={<ModalLoader />}>
          <AddStockModal
            type="SWATCH"
            onClose={handleCloseAddStock}
            onSuccess={handleAddStockSuccess}
          />
        </Suspense>
      )}

      {showDispatch && (
        <Suspense fallback={<ModalLoader />}>
          <DispatchModal
            type="SWATCH"
            inventory={inventory}
            onClose={handleCloseDispatch}
            onSuccess={handleDispatchSuccess}
          />
        </Suspense>
      )}

      {selectedItem && (
        <Suspense fallback={<ModalLoader />}>
          <ItemDetailModal
            item={selectedItem}
            type="SWATCH"
            onClose={handleCloseDetail}
            onRefresh={handleRefresh}
          />
        </Suspense>
      )}
    </div>
  );
};

export default SwatchesDepartment;
